/**
 * La fiche d'une vidéo — **c'est ici que le suspense se dénoue** ⟦A-8⟧.
 *
 * *« Ce que tu as fait »* est dit à la publication ; *« ce que le public en fait »* arrive au
 * fil des jours, et c'est cette page qui le raconte. En mode « réel », la courbe ne montre que
 * **ce qui est déjà tombé** : elle se remplit d'un jour à chaque nuit, et la bibliothèque a
 * enfin une raison d'être ouverte — elle n'en avait aucune, tout étant révélé à la publication.
 *
 * En mode « arcade », la courbe entière est visible dès le départ : le mode ne change **rien**
 * au moteur (A-8, contrainte non négociable), il change ce qu'on affiche. Basculer est
 * instantané et rétroactif, sans le moindre appel réseau.
 *
 * Les deux règles de libellé de l'avenant valent ici aussi : **jours absolus** (« du J3 au
 * J16 », jamais « sur 14 jours »), et « **vues attendues en tout** », jamais « pic de vues ».
 *
 * ⟦C57 · lot 5d · A-174⟧ **Moins de texte.** La fiche ne garde que la courbe, trois nombres (quatre
 * en arcade) et des phrases de 12 mots au plus (`textes-courts.ts`). Les notes d'avant (ce que veut
 * dire « Exécution », le versement de la rente à chaque nuit, ce qu'un remaster relance, la vidéo
 * privée qui ne rapporte plus) sont derrière le pictogramme « 📰 Les dessous » de la fiche, mot pour
 * mot. Le dépliant « ▸ les coulisses » (Chance, Conversion, Qualité, Accroche) reste, tel que
 * SPEC-UI §3.4 le décrit : ce sont des nombres, pas du texte.
 */

import { useState, type ReactNode } from "react";

import {
  H_SEMAINE,
  RENTE_SATURATION_RANG,
  decayEvergreen,
  statutAffichage,
  type GameState,
  type VideoRecord,
} from "@maxime/shared";

import { CourbeVues } from "../Atelier/CourbeVues";
import { IconeAsset } from "../../components/IconeAsset";
import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { contenu } from "../../game/content";
import { BADGE_CLASSE, jourSimule, nombre } from "../../game/format";
import { prixDeLaVideo } from "../../game/marche";
import type { ModeRevelation } from "../../stores/uiStore";

import { courbeVideo } from "./courbe";
import type { RenteVideo } from "./rente";
import {
  attenteCourbeCourte,
  ligneRangCourte,
  ligneRenteVideoCourte,
  noteJoursCourte,
} from "./textes-courts";

function pourcent(v: number, decimales = 2): string {
  return `${(v * 100).toFixed(decimales).replace(".", ",")} %`;
}

export function FicheVideo({
  video,
  titre,
  state,
  mode,
  rente,
  monetise,
  actions,
}: {
  video: VideoRecord;
  titre: string;
  state: GameState;
  mode: ModeRevelation;
  /** La ligne de rente de cette vidéo, si elle en touche une (§6.3). */
  rente: RenteVideo | null;
  monetise: boolean;
  /** Les boutons d'action, montés par la Bibliothèque (elle seule sait ce qui est débloqué). */
  actions: ReactNode;
}) {
  const [coulisses, setCoulisses] = useState(false);
  const badge = BADGE_CLASSE[video.class];
  const jourPublication = jourSimule(video.published_at_h);
  const courbe = courbeVideo(video, jourPublication);
  const tout = mode === "arcade";
  const privee = video.visibility === "prive";
  // ⟦C15 · A-135 · A-136⟧ Le thème du sujet couvre les 12 miniatures produites (`thumb-<theme>
  // .png`) — couverture complète, les 60 sujets de `content/subjects.json` portent chacun l'un
  // des 12 `ThemeId`. `IconeAsset` reste le filet de sécurité si un sujet venait à manquer.
  const theme = contenu().parSujet.get(video.subject_id)?.theme;

  // Ce qu'on trace : en arcade, la courbe entière ; en réel, seulement ce qui est tombé.
  const tracee = tout ? courbe.complete : courbe.realisee;

  return (
    <aside className="biblio-fiche carte" aria-label={`Fiche de « ${titre} »`}>
      {theme ? (
        <div className="biblio-fiche-vignette">
          <IconeAsset src={`/miniature/thumb-${theme}.png`} loadingLazy={false} />
        </div>
      ) : null}
      <div className="biblio-fiche-tete">
        <p className="etiquette">
          {video.format === "short" ? "short · 9:16" : "vidéo longue · 16:9"}
        </p>
        {/* ⟦C57 · lot 5d⟧ Les notes de la fiche, derrière le pictogramme du Journal. */}
        <LesDessous titre={titre} variante="icone" className="biblio-fiche-dessous">
          <DessousBloc titre="La courbe">
            <p className="dessous-texte">
              {tracee.length === 1
                ? `Premier jour encaissé, au J${courbe.premierJour}. La courbe se dessine à partir du prochain.`
                : tracee.length === 0 && courbe.joursRestants > 0
                  ? `Rien n'est encore tombé. Les premières vues arrivent au J${courbe.premierJour}.`
                  : tracee.length === 0
                    ? "Cette vidéo n'a rien rapporté. Ça arrive. Ça arrive même souvent."
                    : tout
                      ? "En mode arcade, la courbe entière est visible dès le départ."
                      : "La courbe ne montre que ce qui est déjà tombé : elle se remplit d'un jour à chaque nuit."}
            </p>
            {courbe.joursRestants > 0 ? (
              <p className="dessous-texte">
                {tout ? (
                  <>
                    Versement du J{courbe.premierJour} au J{courbe.dernierJour} —{" "}
                    {courbe.joursRestants} jour{courbe.joursRestants > 1 ? "s" : ""} pas encore
                    encaissé{courbe.joursRestants > 1 ? "s" : ""}.
                  </>
                ) : (
                  <>
                    Il reste {courbe.joursRestants} jour{courbe.joursRestants > 1 ? "s" : ""} à
                    tomber, jusqu'au J{courbe.dernierJour}. Dors : la courbe avancera d'un cran.
                  </>
                )}
                {privee ? " Mais elle est privée : le temps passe, plus rien n'est versé." : ""}
              </p>
            ) : null}
          </DessousBloc>
          <DessousBloc titre="Les nombres">
            <DessousLignes
              lignes={[
                ...(tout
                  ? [{ icone: "🎯", texte: "Vues attendues en tout : fenêtre initiale, hors rente." }]
                  : []),
                { icone: "🎬", texte: "Exécution : ce que TU as fait." },
              ]}
            />
          </DessousBloc>
          {rente ? (
            <DessousBloc titre="La rente">
              <p className="dessous-texte">
                {nombre(rente.vues)} vues · {nombre(rente.abonnes)} abonné
                {rente.abonnes >= 2 ? "s" : ""}
                {monetise ? ` · ${nombre(rente.argent)} ₵` : ""} — versés à chaque nuit, au prorata
                des heures dormies ({H_SEMAINE} h éveillées = une semaine).
              </p>
              <p className="dessous-texte">
                Rang {rente.rang} de ta bibliothèque · elle a gardé{" "}
                {pourcent(decayEvergreen(video, state.clock_h), 0)} de sa force
                {rente.saturation < 1
                  ? ` · retenue à ${pourcent(rente.saturation, 0)} : tu as plus de ${RENTE_SATURATION_RANG} evergreens actives`
                  : ""}
                . Un <strong>remaster</strong> relance sa demi-vie.
              </p>
            </DessousBloc>
          ) : null}
        </LesDessous>
      </div>
      {/* ⟦C57 · lot 5d⟧ Un NOM de vidéo (un contenu), pas un titre d'interface : un paragraphe qui porte
          le rôle d'un titre de niveau 2, pour les lecteurs d'écran. */}
      <p className="biblio-fiche-titre" role="heading" aria-level={2}>
        « {titre} »
      </p>
      <p className="biblio-fiche-sous-titre">
        {badge.icone} {statutAffichage(video, state.clock_h)} · publiée au J{jourPublication}
        {privee ? " · passée en privé" : ""}
      </p>

      {/* ── La courbe, telle qu'elle se remplit ──────────────────────────────
          ⚠️ **Deux points minimum.** `CourbeVues` trace une aire jusqu'à
          `points[visibles - 1]` avec `visibles = max(2, …)` : sur une courbe d'un seul point,
          il lit `points[1]`, qui n'existe pas. L'écran de publication ne peut pas tomber
          dessus (la plus courte distribution du §6.2 fait 3 jours), la bibliothèque si — dès
          le premier jour versé. On garde donc le composant intact et on ne l'appelle qu'à
          partir de deux points ; en dessous, un mot suffit, il n'y a pas de courbe à lire. */}
      {tracee.length >= 2 ? (
        <CourbeVues courbe={tracee} anime={false} />
      ) : (
        <p className="biblio-fiche-attente">
          {attenteCourbeCourte({
            tracees: tracee.length,
            joursRestants: courbe.joursRestants,
            premierJour: courbe.premierJour,
          })}
        </p>
      )}

      {courbe.joursRestants > 0 ? (
        <p className="biblio-fiche-note">
          {noteJoursCourte({
            tout,
            joursRestants: courbe.joursRestants,
            premierJour: courbe.premierJour,
            dernierJour: courbe.dernierJour,
          })}
        </p>
      ) : null}

      {/* ── Ce qui est acquis : les chiffres du moteur, jamais reconstruits ── */}
      <dl className="biblio-tuiles">
        <div>
          <dt>Vues encaissées</dt>
          <dd>{nombre(video.total_views)}</dd>
        </div>
        <div>
          <dt>Abonnés rapportés</dt>
          <dd>{nombre(video.total_subs)}</dd>
        </div>
        {tout ? (
          <div>
            <dt>Vues attendues en tout</dt>
            <dd>{nombre(video.vues_pic)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Exécution</dt>
          <dd>{Math.round(video.execution_score)}</dd>
        </div>
      </dl>

      {/* ── La rente : le premier revenu passif du jeu (§6.3) ───────────────── */}
      {rente ? (
        <div className="biblio-rente-fiche">
          <p className="biblio-fiche-sur-titre">🌲 Sa rente, par semaine simulée</p>
          <p className="biblio-fiche-note">
            {ligneRenteVideoCourte({
              vues: rente.vues,
              abonnes: rente.abonnes,
              argent: rente.argent,
              monetise,
            })}
          </p>
          <p className="biblio-fiche-note discret">
            {ligneRangCourte({
              rang: rente.rang,
              force: decayEvergreen(video, state.clock_h),
              retenue: rente.saturation < 1 ? rente.saturation : null,
            })}
          </p>
        </div>
      ) : null}

      {actions}

      {/* ⟦A-8⟧ Les rouages derrière un dépliant : « Chance » dit au joueur que son résultat
          vient du dé, « Conversion » est un rouage interne. On les garde pour qui veut
          regarder sous le capot — jamais dans la face du joueur. */}
      <div className="coulisses">
        <button
          type="button"
          className="lien biblio-lien"
          aria-expanded={coulisses}
          onClick={() => setCoulisses((v) => !v)}
        >
          {coulisses ? "▾" : "▸"} les coulisses
        </button>
        {coulisses ? (
          <dl className="biblio-tuiles coulisses-chiffres">
            <div>
              <dt>Chance</dt>
              <dd>×{video.chance.toFixed(2).replace(".", ",")}</dd>
            </div>
            <div>
              <dt>Conversion</dt>
              <dd>{pourcent(video.taux_conversion)}</dd>
              {/* ⟦A-38⟧ Le même chiffre, à l'endroit où il se lit : le prix d'un abonné EN VUES
                  (SPEC-MOTEUR §4.6). Un pourcentage à deux décimales ne dit rien à personne ;
                  « un abonné = 159 vues » se compare d'une vidéo à l'autre, et c'est là qu'on
                  voit la marche monter. Lecture exacte du TC figé, pas un recalcul. */}
              <span>un abonné = {nombre(prixDeLaVideo(video))} vues</span>
            </div>
            <div>
              <dt>Qualité</dt>
              <dd>{video.qualite_eff.toFixed(2).replace(".", ",")}</dd>
            </div>
            <div>
              <dt>Accroche</dt>
              <dd>{video.accroche_eff.toFixed(2).replace(".", ",")}</dd>
            </div>
          </dl>
        ) : null}
      </div>
    </aside>
  );
}
